import React, { useState, useEffect, useContext, useCallback } from "react"
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom"
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  IconButton,
  Text,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import { X, MapPin, Briefcase, GraduationCap, ChevronLeft, ChevronRight } from "lucide-react";
import { GetUserById } from "../services/backend"
import { resolveImageUrl, resolveProfileImageUrl } from "../util/profileImage"
import { Center } from "@chakra-ui/react"
import ProfilesLoadingRadar from "./ProfilesLoadingRadar"
import ProfileDetailsInterestBadge from "./ui/ProfileDetailsInterestbadge"
import { useToast } from "@chakra-ui/react"
import { GetLikesNotifications, GetMatchesNotifications } from "../services/backend"
import { GlobalContext } from "../store/global_context";
import { AddLike } from "../services/backend"
import { useTranslation } from "react-i18next"
import { useMatchCelebration } from "../context/MatchCelebrationContext"
import CountryFlag from "./ui/CountryFlag"
import { AuthContext } from "../store/auth-context"
import { displayFirstName } from "../util/displayName"
import PremiumVerifiedBadge from "./ui/PremiumVerifiedBadge"
import { displayLifestyleValue } from "../util/lifestyleDefaults"
import { isUserPremium } from "../util/premiumAccess"
import { FREE_DAILY_LIKE_LIMIT, isLikeLimitError } from "../util/likeLimit"

// Replace Next.js Image component with regular img tag
const Image = ({ src, alt, fill, className, priority, ...props }) => {
  return (
    // biome-ignore lint/a11y/useAltText: <explanation>
<img 
      src={src || "https://via.placeholder.com/400x600/cccccc/666666?text=Photo"} 
      alt={alt || "Profile Image"} 
      aria-label="Profile Image"
      className={`${fill ? 'absolute inset-0 w-full h-full' : ''} ${className}`}
      {...props}
    />
  )
}


export default function ProfileDetails() {
  const toast = useToast()
  const { t } = useTranslation()
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [userDataDb, setUserDataDb] = useState();
  const { id } = useParams();
  const [likesNotifications, setLikesNotifications] = useState();
  const [matchesNotifications, setMatchesNotifications] = useState();
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isAdminView =
    searchParams.get("admin") === "1" || location.state?.adminView === true
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false)
  const { celebrateFromLikeResponse } = useMatchCelebration()
  const globalCtx = useContext(GlobalContext);
  const authCtx = useContext(AuthContext);
  const user = authCtx.authenticatedUser;

  const pageBg = useColorModeValue("gray.50", "#0a0a0b");
  const titleColor = useColorModeValue("gray.900", "white");
  const subtitleColor = useColorModeValue("gray.500", "whiteAlpha.600");
  const mutedColor = useColorModeValue("gray.500", "whiteAlpha.600");
  const bodyColor = useColorModeValue("gray.700", "whiteAlpha.800");
  const backBtnBorder = useColorModeValue("gray.300", "whiteAlpha.200");
  const backBtnHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const backBtnColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const sectionDivider = useColorModeValue("rgba(15,23,42,0.08)", "rgba(255,255,255,0.08)");
  const ctaShadow = useColorModeValue(
    "0 4px 16px rgba(52,211,153,0.22)",
    "0 6px 22px rgba(52,211,153,0.28), 0 2px 8px rgba(253,224,71,0.08)"
  );
  const stickyBarBg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(14,14,16,0.92)");
  const stickyBarBorder = useColorModeValue("rgba(15,23,42,0.08)", "rgba(255,255,255,0.06)");
  const stickyBarSolidBg = useColorModeValue("rgba(255,255,255,0.96)", "#000000");
  const stickyBarFadeGradient = useColorModeValue(
    "linear(to-t, rgba(255,255,255,0.96), rgba(255,255,255,0))",
    "linear(to-t, rgba(0,0,0,0.95), rgba(0,0,0,0))"
  );
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false }) ?? false;
  const modalBg = useColorModeValue("white", "surface.900");
  const modalBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const closeBtnBg = useColorModeValue("white", "surface.800");
  const closeBtnColor = useColorModeValue("gray.700", "whiteAlpha.900");
  const closeBtnHoverBg = useColorModeValue("gray.100", "whiteAlpha.200");

  const handleClose = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/shop");
  }, [navigate]);

  const desktopModalContentProps = {
    bg: modalBg,
    borderRadius: "2xl",
    borderWidth: "1px",
    borderColor: modalBorder,
    w: "auto",
    h: "auto",
    maxW: "calc(100vw - 20px)",
    maxH: "calc(100vh - 20px)",
    m: "10px",
    overflow: "visible",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    sx: {
      width: "fit-content",
      height: "fit-content",
    },
  };

  const desktopModalCloseButton = (
    <IconButton
      aria-label={t("common.close")}
      icon={<X size={18} strokeWidth={2.5} />}
      position="absolute"
      top="-3rem"
      right="0"
      size="md"
      w="40px"
      h="40px"
      minW="40px"
      borderRadius="full"
      bg={closeBtnBg}
      color={closeBtnColor}
      borderWidth="1px"
      borderColor={modalBorder}
      boxShadow="lg"
      _hover={{ bg: closeBtnHoverBg }}
      onClick={handleClose}
      zIndex={30}
    />
  );

  function handleOnChat(channelUrl) {
        navigate(`/messages/${channelUrl}`);
    }

        async function handleOnMatchClick() {
                  if (!user?.id) return;

                  try {
                          const likeResponse = await AddLike({
                              LikerProfileId: user.id,
                              LikedProfileId: id,
                          });
                          if (await celebrateFromLikeResponse(likeResponse, userDataDb, { assumeMutualMatch: shouldRenderMatchButton })) {
                            return;
                          }

                      toast({
                        title: t("profile.likeSent"),
                        status: 'success',
                        duration: 10000,
                        isClosable: true,
                        position: "bottom-left",
                    });
                  if (!isUserPremium(user)) {
                    globalCtx.incrementLikeCount();
                  }
                          console.log('Like response:', likeResponse);
                  } catch (error) {
                      console.error('Error in handleOnLike:', error);
                      if (isLikeLimitError(error)) {
                        globalCtx.updateLikeCount(FREE_DAILY_LIKE_LIMIT);
                        toast({
                          title: t("profile.likeLimitReached", {
                            defaultValue: "Daily like limit reached",
                          }),
                          description: t("profile.likeLimitUpgrade", {
                            defaultValue: "Upgrade to Premium for unlimited likes.",
                          }),
                          status: "info",
                          duration: 5000,
                          isClosable: true,
                          position: "bottom-left",
                        });
                      }
                  }

        }

   // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
        useEffect(() => {
          if (!user?.id) return;
          async function GetNotifications() {
            try {
              const likesNotifications = await GetLikesNotifications(user.id);
              const matchesNotifications = await GetMatchesNotifications(user.id);
              setLikesNotifications(likesNotifications);
              setMatchesNotifications(matchesNotifications);
              console.log('Likes Notifications:', likesNotifications);
              console.log('Matches Notifications:', matchesNotifications);
            } catch (error) {
              toast({
                  position: "bottom-left",
               title: t("likes.fetchFailed"),
                description: error.message || t("likes.fetchFailedDescription"),
                status: "error",
                duration: 15000,
                isClosable: true,
              });
            }
          }
          GetNotifications();
        }, [user?.id, toast, t]);

// biome-ignore lint/correctness/useExhaustiveDependencies: reload whenever route :id changes
useEffect(() => {
  let cancelled = false;
  setUserDataDb(undefined);
  setCurrentImageIndex(0);

  async function GetUserInfo() {
    if (!id) return;
    try {
      const profile = await GetUserById(id);
      if (!cancelled) setUserDataDb(profile);
    } catch (err) {
      console.error("Failed to load profile", err);
      if (!cancelled) setUserDataDb(null);
    }
  }

  GetUserInfo();
  return () => {
    cancelled = true;
  };
}, [id]);

  // Handle click on image segments
  const handleImageClick = (e) => {
    e.stopPropagation()
    setIsImageViewerOpen(true)
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === userDataDb.images.length - 1 ? 0 : prev + 1))
  }

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? userDataDb.images.length - 1 : prev - 1))
  }

  const loadingView = (
    <ProfilesLoadingRadar
      minH={isMobile ? "70vh" : "12rem"}
      title={t("profile.pullingTitle")}
      subtitle={t("profile.pullingSubtitle")}
    />
  );

  if (!userDataDb) {
    if (isMobile) {
      return (
        <Center w="100%" minH="100vh" bg={pageBg}>
          {loadingView}
        </Center>
      );
    }
    return (
      <Modal isOpen onClose={handleClose} isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
        <ModalContent {...desktopModalContentProps}>
          {desktopModalCloseButton}
          <ModalBody py={8} px={4} borderRadius="2xl" overflowY="auto" overflow="hidden" flex="1 1 auto" minH={0}>
            {loadingView}
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  let userIds ;
  let shouldRenderMatchButton = false;
  let MatchesUserIds ;
  let ShouldRenderMessageButton = false;
  let sendBirdUri;
 

  console.log('Match Nots',matchesNotifications)

  if(matchesNotifications){
  const matchFound = matchesNotifications.filter(x => x.user.id === id);
  sendBirdUri = matchFound?.[0]?.sendBirdUri ?? "defaultUri";
  }

  if (matchesNotifications) {
    MatchesUserIds = matchesNotifications.map((notif) => notif.user.id);
    userIds = likesNotifications?.map((notif) => notif.user.id) ?? [];
    ShouldRenderMessageButton =
      MatchesUserIds?.includes?.(id) ||
      (MatchesUserIds?.includes?.(id) && !userIds?.includes?.(id));
  }

  if (likesNotifications) {
    MatchesUserIds = matchesNotifications?.map((notif) => notif.user.id) ?? [];
    userIds = likesNotifications.map((notif) => notif.user.id);
    shouldRenderMatchButton = userIds?.includes?.(id) && !MatchesUserIds?.includes?.(id);
  }



  const primaryAction = shouldRenderMatchButton
    ? { label: t("shop.match"), onClick: handleOnMatchClick }
    : ShouldRenderMessageButton
    ? { label: t("shop.message"), onClick: () => handleOnChat(sendBirdUri) }
    : { label: t("shop.like"), onClick: handleOnMatchClick };

  const profileContent = (
    <Box
      w="100%"
      bg={isMobile ? pageBg : "transparent"}
      pb={{ base: "calc(160px + env(safe-area-inset-bottom, 0px))", lg: 4 }}
      minH={{ base: "100vh", lg: "auto" }}
    >
      <Box maxW={isMobile ? "7xl" : "6xl"} mx="auto" p={{ base: 0, md: isMobile ? 4 : 0 }}>
          <Grid templateColumns={{ base: "1fr", lg: "repeat(5, minmax(0, 1fr))" }} gap={{ base: 0, lg: 4 }} w={isMobile ? "full" : "max-content"} maxW="6xl" mx="auto">
            {/* Left Side - Tinder-style Image Gallery */}
            <GridItem colSpan={{ base: 1, lg: 2 }} position="relative">
              <Box
                position="relative"
                w="100%"
                mx={{ base: 0, lg: "auto" }}
                maxW={{ base: "100%", lg: "md" }}
                bg="black"
                borderRadius={{ base: 0, lg: "2xl" }}
                overflow="hidden"
                boxShadow={{ base: "none", lg: "lg" }}
                sx={{ aspectRatio: isMobile ? "4 / 5" : "3 / 4" }}
              >
                {/* Image */}
                <div className="relative h-full w-full cursor-pointer" style={{ position: "absolute", inset: 0 }} onClick={handleImageClick}>
                  <Image
                    src={
                      userDataDb?.images?.[currentImageIndex]
                        ? resolveImageUrl(userDataDb.images[currentImageIndex]) ?? "/placeholder.svg"
                        : resolveProfileImageUrl(userDataDb) ?? "/placeholder.svg"
                    }
                    alt={`${userDataDb.firstName}'s profile`}
                    fill
                    className="object-cover"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    priority
                  />

                  {/* Gradient overlay at bottom */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                  {/* Top indicators */}
                  <div className="absolute top-3 left-3 right-3 flex gap-1 z-10">
                    {userDataDb?.images.map((_, index) => (
                      <div
                        key={index}
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                          index === currentImageIndex ? "bg-white" : "bg-white/30"
                        }`}
                      />
                    ))}
                  </div>

                  {/* Navigation arrows */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      prevImage()
                    }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1.5 transition-all duration-200 backdrop-blur-sm z-10"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      nextImage()
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1.5 transition-all duration-200 backdrop-blur-sm z-10"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  {/* Bottom content overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white z-10">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <h1
                          className="text-2xl font-bold truncate min-w-0"
                          title={userDataDb?.firstName || undefined}
                        >
                          {displayFirstName(userDataDb?.firstName)}
                          {userDataDb?.age != null && userDataDb?.age !== "" ? (
                            <span className="font-bold">, {userDataDb.age}</span>
                          ) : null}
                        </h1>
                        <PremiumVerifiedBadge user={userDataDb} size={20} />
                      </div>

                      {userDataDb?.country?.trim() ? (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="text-xs">{userDataDb.country}</span>
                        <CountryFlag country={userDataDb.country} size={11} />
                      </div>
                      ) : null}

                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <Briefcase className="w-3 h-3" />
                          <span className="text-xs">{userDataDb.occupation}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <GraduationCap className="w-3 h-3" />
                          <span className="text-xs">{userDataDb.education}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Box>
            </GridItem>

            {/* Right Side - Profile Information */}
            <GridItem colSpan={{ base: 1, lg: 3 }} px={{ base: 4, md: 0 }} pt={{ base: 5, lg: 0 }}>
              {(() => {
                const lifestyleItems = [
                  [t("profile.labelDrinking"), displayLifestyleValue(userDataDb.drinking)],
                  [t("profile.labelSmoking"), displayLifestyleValue(userDataDb.smoking)],
                  [t("profile.labelWorkout"), displayLifestyleValue(userDataDb.workout)],
                  [t("profile.labelReligion"), displayLifestyleValue(userDataDb.religion)],
                ];

                return (
                  <Box w="100%" px={{ base: 1, md: 2 }}>
                    {/* Header */}
                    <Flex justify="space-between" align="flex-start" gap={4}>
                      <Box flex="1" minW={0}>
                        <Flex align="center" gap={2} flexWrap="nowrap" minW={0} maxW="100%">
                          <Text
                            as="h1"
                            fontSize={{ base: "3xl", md: "4xl" }}
                            fontWeight="semibold"
                            color={titleColor}
                            letterSpacing="-0.03em"
                            lineHeight="1"
                            noOfLines={1}
                            minW={0}
                            title={userDataDb?.firstName || undefined}
                          >
                            {displayFirstName(userDataDb?.firstName)}
                          </Text>
                          {userDataDb?.age != null && userDataDb?.age !== "" ? (
                            <Text as="span" color={mutedColor} fontWeight="400" flexShrink={0} fontSize={{ base: "3xl", md: "4xl" }} lineHeight="1">
                              {userDataDb.age}
                            </Text>
                          ) : null}
                          <PremiumVerifiedBadge user={userDataDb} size={22} />
                        </Flex>
                        {userDataDb?.country?.trim() ? (
                        <Flex mt={2} align="center" gap={2}>
                          <Text fontSize="sm" color={mutedColor} textTransform="capitalize" m={0}>
                            {userDataDb.country.toLowerCase()}
                          </Text>
                          <CountryFlag country={userDataDb.country} size={16} />
                        </Flex>
                        ) : null}
                      </Box>
                      {!isAdminView && (
                      <Button
                        onClick={primaryAction.onClick}
                        size="md"
                        h="40px"
                        px={6}
                        fontWeight="semibold"
                        borderRadius="full"
                        bgGradient="linear(to-r, brand.300, brand.500)"
                        color="gray.900"
                        boxShadow={ctaShadow}
                        _hover={{
                          bgGradient: "linear(to-r, brand.400, brand.600)",
                          transform: "translateY(-1px)",
                        }}
                        _active={{ transform: "translateY(0)" }}
                        transition="all 0.2s"
                        flexShrink={0}
                        display={{ base: "none", lg: "inline-flex" }}
                      >
                        {primaryAction.label}
                      </Button>
                      )}
                    </Flex>

                    {/* About */}
                    <Box mt={8}>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                        letterSpacing="0.1em"
                        color={mutedColor}
                        mb={2}
                      >
                        {t("profile.about")}
                      </Text>
                      <Text fontSize="sm" lineHeight="tall" color={bodyColor}>
                        {userDataDb?.bio || t("profile.noBio")}
                      </Text>
                    </Box>

                    {/* Lifestyle */}
                    {lifestyleItems.length > 0 && (
                      <Box mt={8}>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="0.1em"
                          color={mutedColor}
                          mb={2}
                        >
                          {t("profile.lifestyleHeading")}
                        </Text>
                        <Box>
                          {lifestyleItems.map(([label, value]) => (
                            <Flex
                              key={label}
                              align="center"
                              justify="space-between"
                              gap={4}
                              py={3}
                              borderBottomWidth="1px"
                              borderBottomColor={sectionDivider}
                              fontSize="sm"
                            >
                              <Text color={mutedColor}>{label}</Text>
                              <Text fontWeight="medium" color={titleColor} textAlign="right" noOfLines={1}>
                                {t(`getStarted.lifestyleOptions.${value}`, { defaultValue: value })}
                              </Text>
                            </Flex>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Interests */}
                    {userDataDb.interests?.length > 0 && (
                      <Box mt={8}>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="0.1em"
                          color={mutedColor}
                          mb={3}
                        >
                          {t("profile.interestsHeading")}
                        </Text>
                        <Flex gap={1.5} flexWrap="wrap">
                          {userDataDb.interests.map((interest) => (
                            <ProfileDetailsInterestBadge key={interest.interestName} interest={interest} />
                          ))}
                        </Flex>
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </GridItem>
          </Grid>
        </Box>

        {/* Mobile sticky bottom CTA — floats above the bottom nav dock */}
        {!isAdminView && (
        <Box
          display={{ base: "block", lg: "none" }}
          position="fixed"
          left={0}
          right={0}
          zIndex={1099}
          bg={stickyBarSolidBg}
          sx={{
            bottom: "calc(56px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <Box
            position="absolute"
            left={0}
            right={0}
            bottom="100%"
            h="56px"
            pointerEvents="none"
            bgGradient={stickyBarFadeGradient}
          />
          <Flex align="center" justify="space-between" gap={3} px={4} pt={3} pb={8}>
            <IconButton
              aria-label={t("profile.passAria")}
              icon={<X size={22} />}
              variant="outline"
              borderRadius="full"
              h="52px"
              minW="52px"
              w="52px"
              borderColor={backBtnBorder}
              color={backBtnColor}
              bg={stickyBarBg}
              backdropFilter="blur(16px)"
              boxShadow="0 6px 18px rgba(0,0,0,0.18)"
              _hover={{ bg: backBtnHoverBg }}
              _active={{ transform: "scale(0.95)" }}
              onClick={() => navigate(-1)}
            />
            <Button
              onClick={primaryAction.onClick}
              flex="1"
              h="52px"
              fontWeight="semibold"
              borderRadius="full"
              bgGradient="linear(to-r, brand.300, brand.500)"
              color="gray.900"
              boxShadow={ctaShadow}
              _hover={{ bgGradient: "linear(to-r, brand.400, brand.600)" }}
              _active={{ transform: "scale(0.98)" }}
              transition="all 0.18s"
            >
              {primaryAction.label}
            </Button>
          </Flex>
        </Box>
        )}

        {/* Full Screen Image Viewer Modal */}
        <Modal isOpen={isImageViewerOpen} onClose={() => setIsImageViewerOpen(false)} size="full">
          <ModalOverlay bg="blackAlpha.900" />
          <ModalContent 
            bg="transparent" 
            boxShadow="none" 
            maxW="100vw" 
            maxH="100vh"
            m={0}
            p={0}
          >
            <ModalBody 
              p={0} 
              position="relative" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              h="100vh"
              w="100vw"
            >
              {/* Close button */}
              <IconButton
                aria-label="Close viewer"
                icon={<X className="w-6 h-6" />}
                position="absolute"
                top={4}
                right={4}
                onClick={() => setIsImageViewerOpen(false)}
                bg="blackAlpha.500"
                color="white"
                _hover={{ bg: "blackAlpha.600" }}
                zIndex={2}
              />

              {/* Image */}
              <div className="relative w-full h-full flex items-center justify-center">
                <img
                  src={
                      userDataDb?.images?.[currentImageIndex]
                      ? resolveImageUrl(userDataDb.images[currentImageIndex]) ?? "/placeholder.svg"
                      : resolveProfileImageUrl(userDataDb) ?? "/placeholder.svg"
                  }
                  alt={`${userDataDb.firstName}'s profile`}
                  className="max-w-full max-h-full object-contain"
                  style={{ maxHeight: '90vh' }}
                />
              </div>

              {/* Navigation arrows */}
              <IconButton
                aria-label="Previous image"
                icon={<ChevronLeft className="w-8 h-8" />}
                position="absolute"
                left={4}
                top="50%"
                transform="translateY(-50%)"
                onClick={(e) => {
                  e.stopPropagation()
                  prevImage()
                }}
                bg="blackAlpha.500"
                color="white"
                _hover={{ bg: "blackAlpha.600" }}
                zIndex={2}
              />
              <IconButton
                aria-label="Next image"
                icon={<ChevronRight className="w-8 h-8" />}
                position="absolute"
                right={4}
                top="50%"
                transform="translateY(-50%)"
                onClick={(e) => {
                  e.stopPropagation()
                  nextImage()
                }}
                bg="blackAlpha.500"
                color="white"
                _hover={{ bg: "blackAlpha.600" }}
                zIndex={2}
              />

              {/* Image counter */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-blackAlpha.500 text-white px-3 py-1 rounded-full text-sm">
                {currentImageIndex + 1} / {userDataDb?.images?.length}
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
    </Box>
  );

  if (isMobile) {
    return (
      <Center w="100%">
        {profileContent}
      </Center>
    );
  }

  return (
    <Modal
      isOpen
      onClose={handleClose}
      isCentered
      scrollBehavior="inside"
      closeOnOverlayClick
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
      <ModalContent {...desktopModalContentProps}>
        {desktopModalCloseButton}
        <ModalBody p={4} borderRadius="2xl" overflowY="auto" overflow="hidden" flex="1 1 auto" minH={0}>
          {profileContent}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}